import { useState } from "react";
import { useParams } from "react-router";
import { ArrowRightIcon } from "vega-icons";

import { useUpdateDealV2 } from "@vega-api/generated/deal/deal";
import { type DealDataRoom } from "@vega-api/generated/types/dealDataRoom";
import { type DealRequestV2 } from "@vega-api/generated/types/dealRequestV2";
import { type DealResponseV2 } from "@vega-api/generated/types/dealResponseV2";
import { ThemedButton } from "@vega-core/components/Buttons/ThemedButton";
import { useToast } from "@vega-core/components/Toast/useToast";
import { useBoolean } from "@vega-core/hooks/useBoolean";
import { m } from "@vega-core/paraglide/messages";
import { isGPCoInvestPortal } from "@vega-core/utils/global";
import { useCoInvestErrorToast } from "@vega-modules/hooks/useCoInvestErrorToast";
import { useFeatureFlagGuard } from "@vega-modules/hooks/useFeatureFlagGuard";
import { CoInvestmentFullPaths, CommonPaths } from "@vega-modules/paths";

import { AddDataRoomModal } from "../components/AddDataRoomModal/AddDataRoomModal";
import { CoInvestmentPageHeader } from "../components/CoInvestmentPageHeader/CoInvestmentPageHeader";
import { DataRoomProvidersSection } from "../components/DataRoomProvidersSection/DataRoomProvidersSection";
import { EditDataRoomModal } from "../components/EditDataRoomModal/EditDataRoomModal";
import { NdaUploadSection } from "../components/NdaUploadSection/NdaUploadSection";
import { RemoveDataRoomModal } from "../components/RemoveDataRoomModal/RemoveDataRoomModal";
import { useDealName } from "../hooks/useDealName";
import { useNdaUpload } from "../hooks/useNdaUpload";

const buildUpdateBody = (
  deal: DealResponseV2,
  updatedDataRooms: DealDataRoom[],
): DealRequestV2 => ({
  name: deal.name,
  description: deal.description,
  sector: deal.sector,
  region: deal.region,
  targetAmount: deal.targetAmount,
  generalPartnerId: deal.generalPartnerId,
  fundId: deal.fundId,
  assetClass: deal.assetClass,
  currency: deal.currency,
  securityType: deal.securityType,
  closingDate: deal.closingDate,
  dealOverview: deal.dealOverview
    ? {
        sections: deal.dealOverview.sections ?? [],
        metrics: deal.dealOverview.metrics ?? [],
      }
    : undefined,
  dealDataRooms: updatedDataRooms,
});

export const DataRoomAndNdaPage = () => {
  const { id: dealIdParams } = useParams<{ id: string }>();
  const dealId = dealIdParams ?? "";

  useFeatureFlagGuard(
    "gp_co_investment",
    isGPCoInvestPortal ? undefined : CommonPaths.Home,
  );
  const showToast = useToast();
  const showErrorToast = useCoInvestErrorToast();

  const { dealName, isLoading: isDealNameLoading } = useDealName(dealId);

  const {
    dealGPViewResponse,
    refetchGPView,
    isPendingGPView,
    uploadPdfMutation,
    uploadWordMutation,
    pdfInputRef,
    wordInputRef,
    handlePdfFileSelect,
    handleWordFileSelect,
  } = useNdaUpload(dealId);

  const deal = dealGPViewResponse?.data?.deal;

  const {
    isOpen: isAddOpen,
    setTrue: openAdd,
    setFalse: closeAdd,
  } = useBoolean(false);
  const {
    isOpen: isEditOpen,
    setTrue: openEdit,
    setFalse: closeEdit,
  } = useBoolean(false);
  const {
    isOpen: isRemoveOpen,
    setTrue: openRemove,
    setFalse: closeRemove,
  } = useBoolean(false);
  const [selectedDataRoom, setSelectedDataRoom] = useState<DealDataRoom | null>(
    null,
  );

  const updateDealMutation = useUpdateDealV2();

  const mutateWithDealRooms = ({
    updatedDataRooms,
    successMessage,
    onSuccess,
  }: {
    updatedDataRooms: DealDataRoom[];
    successMessage: string;
    onSuccess: () => void;
  }) => {
    if (!dealId || !deal) return;

    updateDealMutation.mutate(
      { id: dealId, data: buildUpdateBody(deal, updatedDataRooms) },
      {
        onSuccess: () => {
          showToast({
            variant: "success",
            title: m.status_success(),
            description: successMessage,
          });
          refetchGPView();
          onSuccess();
        },
        onError: (error) => {
          showErrorToast(error);
        },
      },
    );
  };

  const handleEditDataRoom = (dataRoom: DealDataRoom) => {
    setSelectedDataRoom(dataRoom);
    openEdit();
  };

  const handleRemoveDataRoom = (dataRoom: DealDataRoom) => {
    setSelectedDataRoom(dataRoom);
    openRemove();
  };

  const handleConfirmAdd = (name: string, url: string) => {
    const newRoom: DealDataRoom = {
      dataRoomProvider: { name, displayName: name },
      dataRoomUrl: url,
    };
    mutateWithDealRooms({
      updatedDataRooms: [...(deal?.dealDataRooms ?? []), newRoom],
      successMessage: m.coInvestments_dataRoom_add_success(),
      onSuccess: closeAdd,
    });
  };

  const handleConfirmEdit = (name: string, url: string) => {
    if (!selectedDataRoom || !deal?.dealDataRooms) return;

    const updatedDataRooms = deal.dealDataRooms.map((room) =>
      room.dataRoomProvider.name === selectedDataRoom.dataRoomProvider.name
        ? { dataRoomProvider: { name, displayName: name }, dataRoomUrl: url }
        : room,
    );

    mutateWithDealRooms({
      updatedDataRooms,
      successMessage: m.coInvestments_dataRoom_edit_success(),
      onSuccess: () => {
        closeEdit();
        setSelectedDataRoom(null);
      },
    });
  };

  const handleConfirmRemove = () => {
    if (!selectedDataRoom || !deal?.dealDataRooms) return;

    const updatedDataRooms = deal.dealDataRooms.filter(
      (room) =>
        room.dataRoomProvider.name !== selectedDataRoom.dataRoomProvider.name,
    );

    mutateWithDealRooms({
      updatedDataRooms,
      successMessage: m.coInvestments_dataRoom_remove_success(),
      onSuccess: () => {
        closeRemove();
        setSelectedDataRoom(null);
      },
    });
  };

  return (
    <>
      <div className="my-4 max-w-3xl mx-auto">
        <CoInvestmentPageHeader
          capitalize={false}
          description={
            <p className="text-text/secondary body-base-regular">
              {m.coInvestments_dataRoomNdaDescription()}
            </p>
          }
          fundName={dealName}
          isLoading={isPendingGPView || isDealNameLoading}
          title={m.coInvestments_nav_dataRoomAndNDA()}
        />

        <div className="space-y-12 mt-8">
          <DataRoomProvidersSection
            dataRooms={deal?.dealDataRooms ?? []}
            isPending={isPendingGPView}
            onAddDataRoom={openAdd}
            onEditDataRoom={handleEditDataRoom}
            onRemoveDataRoom={handleRemoveDataRoom}
          />

          <NdaUploadSection
            isPendingPdf={uploadPdfMutation.isPending}
            isPendingWord={uploadWordMutation.isPending}
            nda={dealGPViewResponse?.data?.nda}
            onPdfFileSelect={handlePdfFileSelect}
            onWordFileSelect={handleWordFileSelect}
            pdfInputRef={pdfInputRef}
            wordInputRef={wordInputRef}
          />
        </div>

        {dealId ? (
          <div className="flex justify-center mt-8">
            <ThemedButton
              className="text-text/secondary body-small-regular"
              dataTestId="manage-lp-specific-ndas"
              icon={<ArrowRightIcon className="size-4" />}
              iconPosition="right"
              route={CoInvestmentFullPaths.CoInvestmentLpNdas.replace(
                ":id",
                dealId,
              )}
              variant="text"
            >
              {m.coInvestments_manageLpSpecificNdas()}
            </ThemedButton>
          </div>
        ) : null}
      </div>

      {isAddOpen ? (
        <AddDataRoomModal
          isUpdatePending={updateDealMutation.isPending}
          onClose={closeAdd}
          onConfirm={handleConfirmAdd}
        />
      ) : null}

      {selectedDataRoom && isEditOpen ? (
        <EditDataRoomModal
          dataRoom={selectedDataRoom}
          isUpdatePending={updateDealMutation.isPending}
          onClose={closeEdit}
          onConfirm={handleConfirmEdit}
        />
      ) : null}
      {selectedDataRoom && isRemoveOpen ? (
        <RemoveDataRoomModal
          dataRoomName={selectedDataRoom.dataRoomProvider.displayName}
          isUpdatePending={updateDealMutation.isPending}
          onClose={closeRemove}
          onConfirm={handleConfirmRemove}
        />
      ) : null}
    </>
  );
};
